import { useEffect, useState } from 'react';
import {
  BrowserRouter,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { SessionProvider, useSession } from './lib/session.tsx';
import { Login } from './pages/Login.tsx';
import { DashboardPage } from './pages/Dashboard.tsx';
import { MembersPage, MemberDetailPage } from './pages/Members.tsx';
import { PostChargePage } from './pages/PostCharge.tsx';
import { TrialBalancePage, DepartmentalPLPage, PeriodsPage } from './pages/Finance.tsx';
import { ChartOfAccountsPage, CoaImportPage } from './pages/ChartOfAccounts.tsx';
import { StatementsPage } from './pages/Statements.tsx';
import { LedgerSyncPage, LedgerSettingsPage, LedgerMappingPage } from './pages/Ledger.tsx';
import { TakePaymentPage, CollectionsPage, BillingRunsPage } from './pages/Billing.tsx';
import { StructurePage } from './pages/Structure.tsx';
import { NewMemberPage } from './pages/NewMember.tsx';
import { MemberImportPage } from './pages/MemberImport.tsx';
import { BirthdaysPage } from './pages/Birthdays.tsx';
import { ApplicationsPage } from './pages/Applications.tsx';
import {
  VendorsPage,
  VendorDetailPage,
  InvoicesPage,
  InvoiceDetailPage,
  NewInvoicePage,
  PaymentRunsPage,
  PaymentRunDetailPage,
  ApprovalRulesPage,
  ApReportsPage,
} from './pages/AccountsPayable.tsx';
import { BylawsPage, BoardPage, MeetingsPage } from './pages/Governance.tsx';
import { StaffPage } from './pages/Staff.tsx';
import { SchedulePage } from './pages/Schedule.tsx';
import { MyTimePage, TimesheetsPage } from './pages/Timesheets.tsx';
import { MyLeavePage, LeavePage } from './pages/Leave.tsx';
import { LaborCostPage } from './pages/LaborCost.tsx';
import { LeaveLiabilityPage } from './pages/LeaveLiability.tsx';
import { HomePage } from './pages/Home.tsx';
import { KitchenPage } from './pages/Kitchen.tsx';
import { PurchasingPage } from './pages/Purchasing.tsx';
import { RecipesPage } from './pages/Recipes.tsx';
import { WeatherStaffingPage } from './pages/WeatherStaffing.tsx';
import { LoginsPage } from './pages/Logins.tsx';
import {
  BylawNoticesPage,
  ClubRulesPage,
  SampleDataPage,
  ExportDataPage,
  ImportDataPage,
  DemoClubsPage,
} from './pages/Settings.tsx';
import { CampusPage } from './pages/Campus.tsx';
import { WaiversPage } from './pages/Waivers.tsx';
import { TeeSheetPage, ScorecardPage } from './pages/TeeSheet.tsx';
import { MyGolfPage } from './pages/MyGolf.tsx';
import { GolfAdminPage } from './pages/GolfAdmin.tsx';
import { ProShopPage } from './pages/ProShop.tsx';
import { PlayingCardPage, ScanLandingPage } from './pages/PlayingCard.tsx';
import { MessagesPage } from './pages/Messages.tsx';
import { CourtSheetPage, MyCourtsPage } from './pages/CourtSheet.tsx';
import { TennisAdminPage } from './pages/TennisAdmin.tsx';
import { DeckSheetPage } from './pages/DeckSheet.tsx';
import { PoolAdminPage } from './pages/PoolAdmin.tsx';
import { MyPoolPage } from './pages/MyPool.tsx';
import { AgronomyPage } from './pages/Agronomy.tsx';
import { MyAccountPage } from './pages/MyAccount.tsx';
import { FnbTerminalPage } from './pages/FnbTerminal.tsx';
import { FnbMenuPage } from './pages/FnbMenu.tsx';
import { FnbServicePage } from './pages/FnbService.tsx';
import { DockSheetPage, TransientLogPage, MyBoatingPage } from './pages/DockSheet.tsx';
import { MarinaAdminPage } from './pages/MarinaAdmin.tsx';
import { RangeSheetPage, MyRangePage } from './pages/RangeSheet.tsx';
import { ShootingAdminPage } from './pages/ShootingAdmin.tsx';
import { DuesSchedulePage, AssessmentsPage, AssessmentDetailPage } from './pages/Assessments.tsx';
import {
  BankAccountsPage,
  BankAccountDetailPage,
  ReconciliationPage,
  NewReconciliationPage,
} from './pages/Banking.tsx';
import { OperatorApp } from './pages/Operator.tsx';
import { clearActingAs, getActingAs } from './lib/operator.tsx';
import { Loading } from './components/ui.tsx';
import { BetterClubLockup, Club1899Wordmark } from './components/brand.tsx';

function Shell() {
  const { club, me, signOut, atLeast, atLeastRung, runs } = useSession();

  // On a phone the sidebar is a drawer. On a desktop this state is
  // ignored entirely — the CSS above 860px never reads it.
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Tapping a link navigates AND closes. Watching the location rather
  // than putting an onClick on twenty-three links means a route reached
  // any other way — a redirect, the back button — closes it too.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  // The page behind a drawer must not scroll under it: on iOS the
  // drawer stays put while the content slides away beneath, which reads
  // as the app having lost its place.
  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className={menuOpen ? 'app app--menu-open' : 'app'}>
      {/* Tapping anywhere off the drawer closes it, which is what every
          phone user tries first. Rendered always so it can fade rather
          than appear. */}
      <div
        className="scrim"
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      <nav className="sidebar" id="main-nav">
        <div className="sidebar__brand">
          {/* The drawn wordmark only fits the club it was drawn for. Any other
              tenant gets its name in type — a mark is not a template. */}
          {club?.brand_name === 'Club 1899' ? (
            <Club1899Wordmark height={24} />
          ) : (
            <strong>{club?.brand_name ?? 'BetterClub'}</strong>
          )}
          <div className="sidebar__spacer" />
          <button
            className="drawer-close"
            onClick={() => setMenuOpen(false)}
            aria-label="Close the menu"
          >
            &#10005;
          </button>
        </div>

        <div className="sidebar__nav">
          {/* A member's own two links, and the condition is an ACCOUNT
              rather than a rung: a greenkeeper with no membership has
              no statement to read, and a club_admin who is also a
              member has one. Role is the wrong question here, and
              `member_account_ids` is the right one — it is on the
              principal precisely because "may see this account" was
              never a rung on the ladder.

              These exist because every member-facing endpoint on them
              already did. `assertCanAccessAccount` has scoped
              statements and payment methods to the caller's own
              accounts since the beginning and /v1/me/contact-preferences
              since 0112, and the only screens over any of them were
              ones that start by listing every member in the club. */}
          {(me?.principal.member_account_ids.length ?? 0) > 0 && (
            <>
              <div className="sidebar__group">Me</div>
              {/* `/my-week`, not `/`. A member's front door IS `/`, but
                  a manager who is also a member gets the operational
                  dashboard there — so a link labelled "My Week"
                  pointing at `/` would show that person the club's
                  dashboard and be a lie in the label. The address is
                  the same page for everybody. */}
              <NavLink to="/my-week" className="navlink">
                My Week
              </NavLink>
              <NavLink to="/my-account" className="navlink">
                My Account
              </NavLink>
            </>
          )}

          {/* A member is not the desk. Every link in this block calls an
              endpoint that refuses them, so showing it is offering a door
              that opens onto a red banner — which reads as broken rather
              than as not-for-you. Found by signing in as an actual member
              and watching /v1/dashboard come back 403. */}
          {atLeast('staff') && (
            <>
              <div className="sidebar__group">Daily</div>
              <NavLink to="/" end className="navlink">
                Dashboard
              </NavLink>
              {/* `end`, or every nested route under /members — a member's own
                  page, the importer — lights this up as well as itself. */}
              <NavLink to="/members" end className="navlink">
                Members
              </NavLink>
              <NavLink to="/members/new" className="navlink">
                Add a Member
              </NavLink>
              <NavLink to="/members/applications" className="navlink">
                Applications
              </NavLink>
              <NavLink to="/members/birthdays" className="navlink">
                Birthdays
              </NavLink>
              <NavLink to="/charges/new" className="navlink">
                Post a Charge
              </NavLink>
              <NavLink to="/payments/new" className="navlink">
                Take a Payment
              </NavLink>
              {/* Staff below manager get their own shifts here; a manager
                  gets the whole schedule under Personnel instead. One
                  link each, at the same address — the route narrows to
                  the caller's own published shifts below manager. */}
              {/* `atLeastRung` (0132): a department head is a
                  supervisor on this screen, not somebody looking up
                  their own rota, and they get the full Schedule link
                  under Personnel below. `atLeast` here would give them
                  both links, at the same address, saying different
                  things. */}
              {!atLeastRung('manager') && (
                <NavLink to="/schedule" className="navlink">
                  My Shifts
                </NavLink>
              )}
              {/* Everybody with an employment, including managers: a
                  head chef clocks in like anybody else, and a separate
                  manager-only version of this page would be a second
                  place to tap in. */}
              <NavLink to="/my-time" className="navlink">
                My Time
              </NavLink>
              <NavLink to="/my-leave" className="navlink">
                My Time Off
              </NavLink>
            </>
          )}

          <div className="sidebar__group">Golf</div>
          <NavLink to="/golf/my" className="navlink">
            My Golf
          </NavLink>
          {atLeast('staff') && (
            <NavLink to="/golf/tee-sheet" className="navlink">
              Tee Sheet
            </NavLink>
          )}
          <NavLink to="/golf/scorecard" className="navlink">
            Course Information
          </NavLink>
          {/* `runs`, not `atLeast` (0132). A department head IS a
              manager — of some departments — so asking about the rung
              here would take the golf section away from the golf
              professional, which is the exact opposite of the point.
              And a scoped manager now fails `atLeast('manager')` by
              design, so this is not a refinement: it is the difference
              between the section appearing and not. */}
          {runs('golf') && (
            <NavLink to="/golf/admin" className="navlink">
              Administration
            </NavLink>
          )}
          {atLeast('staff') && (
            <NavLink to="/shop" className="navlink">
              Pro Shop
            </NavLink>
          )}

          {/* `atLeast('staff')`, not `runs('grounds')`, and the
              difference matters. The recording form on this page is
              gated on the module; the page itself is not, because what
              it leads with is which ground is shut to work — and the
              person that protects is the one about to be sent out
              there. A rule only the grounds manager could see would be
              a rule the crew finds out about from an error message. */}
          {/* The HEADING is inside the gate too. It was outside it, so a
              member saw the words "COURSE MAINTENANCE" with nothing
              under them — a section of the app that appeared to exist
              and had no way in. Found by signing in as a member and
              looking at the sidebar, which is the only way it could
              have been found: an empty heading throws nothing. */}
          {atLeast('staff') && (
            <>
              <div className="sidebar__group">Course Maintenance</div>
              <NavLink to="/grounds" className="navlink">
                What Went On The Ground
              </NavLink>
            </>
          )}

          {/* Racquets, aquatics and shooting each showed a member at
              least one link into a screen that refuses them — the court
              sheet is the desk's, and three Administration links were
              ungated outright. Golf and boating had the gating right;
              these three did not, and the same member who cannot open
              /golf/admin could open /tennis/admin and read the banner.
              A door that opens onto a refusal reads as broken software
              rather than as not-for-you, which is the sentence written
              at the top of the Daily block for the same reason. */}
          <div className="sidebar__group">Racquet Sports</div>
          <NavLink to="/tennis/my-courts" className="navlink">
            My Courts
          </NavLink>
          {atLeast('staff') && (
            <NavLink to="/tennis/court-sheet" className="navlink">
              Court Sheet
            </NavLink>
          )}
          {runs('racquet') && (
            <NavLink to="/tennis/admin" className="navlink">
              Administration
            </NavLink>
          )}

          <div className="sidebar__group">Aquatics</div>
          {/* Aquatics was the one amenity with no member page at all.
              0180–0186 built the water, the guards, the programmes and
              the waiver, and every screen over them was one a member of
              STAFF opens — so a member's only route to a lane was the
              telephone, at the busiest facility most clubs own. */}
          <NavLink to="/pool/my" className="navlink">
            My Pool
          </NavLink>
          {atLeast('staff') && (
            <NavLink to="/pool/deck" className="navlink">
              The Deck
            </NavLink>
          )}
          {runs('aquatics') && (
            <NavLink to="/pool/admin" className="navlink">
              Administration
            </NavLink>
          )}

          <div className="sidebar__group">Shooting Sports</div>
          <NavLink to="/shooting/my" className="navlink">
            My Shooting
          </NavLink>
          {atLeast('staff') && (
            <NavLink to="/shooting/range-sheet" className="navlink">
              Range Sheet
            </NavLink>
          )}
          {runs('shooting') && (
            <NavLink to="/shooting/admin" className="navlink">
              Administration
            </NavLink>
          )}

<div className="sidebar__group">Boating</div>
          <NavLink to="/boating/my" className="navlink">
            My Boating
          </NavLink>
          {atLeast('staff') && (
            <NavLink to="/boating/dock-sheet" className="navlink">
              Dock Sheet
            </NavLink>
          )}
          {atLeast('staff') && (
            <NavLink to="/boating/transient-log" className="navlink">
              Transient Log
            </NavLink>
          )}
          {runs('boating') && (
            <NavLink to="/boating/admin" className="navlink">
              Administration
            </NavLink>
          )}

          {/* A member does not open a check; the terminal is the desk's.
              Showing it to them is a door onto a red banner, which reads
              as broken rather than as not-for-you. */}
          {atLeast('staff') && (
            <>
              <div className="sidebar__group">Food &amp; Beverage</div>
              <NavLink to="/fnb/terminal" className="navlink">
                Terminal
              </NavLink>
              {/* The dining room and the kitchen behind it are two
                  modules (0132), because plenty of clubs have a
                  clubhouse manager and a chef and they are not the same
                  person. A club that runs them as one assigns both to
                  the same department, which is a row rather than a
                  special case. */}
              {runs('fnb') && (
                <>
                  <NavLink to="/fnb/service" className="navlink">
                    Service
                  </NavLink>
                  <NavLink to="/fnb/menu" className="navlink">
                    The Menu
                  </NavLink>
                </>
              )}
              {runs('kitchen') && (
                <>
                  <NavLink to="/fnb/kitchen" className="navlink">
                    Kitchen
                  </NavLink>
                  <NavLink to="/fnb/recipes" className="navlink">
                    Recipes &amp; Food Cost
                  </NavLink>
                </>
              )}
            </>
          )}

          {/* `atLeastRung`, not `atLeast` (0132). These four are the
              screens a department head most needs, and the API narrows
              them to the departments they hold rather than refusing
              them — the roster, the schedule, the timesheets, the
              leave. Gating them on `atLeast('manager')` would hide
              exactly the wrong four, on a rule written to keep a tennis
              professional out of Payables.

              The office sees the same group; for them the narrowing is
              "no narrowing", which is what holding every department
              means. */}
          {atLeastRung('manager') && (
            <>
              <div className="sidebar__group">Personnel</div>
              <NavLink to="/staff" className="navlink">
                Personnel
              </NavLink>
              <NavLink to="/schedule" className="navlink">
                Schedule
              </NavLink>
              <NavLink to="/timesheets" className="navlink">
                Timesheets
              </NavLink>
              <NavLink to="/leave" className="navlink">
                Leave
              </NavLink>
            </>
          )}

          {atLeast('manager') && (
            <>
              <div className="sidebar__group">Finance</div>
              <NavLink to="/finance/trial-balance" className="navlink">
                Trial Balance
              </NavLink>
              <NavLink to="/finance/departmental-pl" className="navlink">
                Departmental P&amp;L
              </NavLink>
              <NavLink to="/finance/statements" className="navlink">
                Statements
              </NavLink>
              <NavLink to="/finance/dues" className="navlink">
                Dues Runs
              </NavLink>
              <NavLink to="/finance/dues-schedule" className="navlink">
                Dues Schedule
              </NavLink>
              <NavLink to="/finance/assessments" className="navlink">
                Assessments
              </NavLink>
              <NavLink to="/finance/collections" className="navlink">
                Collections
              </NavLink>
              <NavLink to="/finance/periods" className="navlink">
                Periods
              </NavLink>
              <NavLink to="/coa" className="navlink">
                Chart of Accounts
              </NavLink>

              <div className="sidebar__group">Payables</div>
              <NavLink to="/purchasing" className="navlink">
                Purchasing
              </NavLink>
              <NavLink to="/ap/invoices" className="navlink">
                Invoices
              </NavLink>
              <NavLink to="/ap/vendors" className="navlink">
                Vendors
              </NavLink>
              {atLeast('controller') && (
                <NavLink to="/ap/runs" className="navlink">
                  Payment Runs
                </NavLink>
              )}
              <NavLink to="/ap/reports" className="navlink">
                Payables Reports
              </NavLink>
              <NavLink to="/ap/approval-rules" className="navlink">
                Approval Thresholds
              </NavLink>

              <div className="sidebar__group">Banking</div>
              <NavLink to="/banking/accounts" className="navlink">
                Bank Accounts
              </NavLink>

              <div className="sidebar__group">The Club</div>
              <NavLink to="/structure" className="navlink">
                Departments &amp; Outlets
              </NavLink>
              <NavLink to="/members/import" className="navlink">
                Import Members
              </NavLink>
              <NavLink to="/messages" className="navlink">
                Messages
              </NavLink>
              {/* The money side of personnel, and the office's. A
                  department head is refused all four by the API — see
                  0132: `fn_labor_cost_totals` and
                  `fn_leave_liability_movement` return every
                  department's figures in one call and take no
                  department to narrow by, so these are not narrowed but
                  withheld. */}
              <NavLink to="/labor-cost" className="navlink">
                Labor Cost
              </NavLink>
              <NavLink to="/leave-liability" className="navlink">
                Leave Liability
              </NavLink>
              <NavLink to="/weather-staffing" className="navlink">
                Weather &amp; Staffing
              </NavLink>
              <NavLink to="/logins" className="navlink">
                Who Can Get In
              </NavLink>

              <div className="sidebar__group">Governance</div>
              <NavLink to="/governance/bylaws" className="navlink">
                Bylaws
              </NavLink>
              <NavLink to="/governance/board" className="navlink">
                The Board
              </NavLink>
              <NavLink to="/governance/meetings" className="navlink">
                Meetings
              </NavLink>

              <div className="sidebar__group">Settings</div>
              {/* Under Settings rather than beside a module, because the
                  whole point of one registry is that it belongs to none
                  of them. `atLeast('manager')` — the enclosing gate —
                  and so a department head scoped to Aquatics does not
                  get to rename the marina. */}
              <NavLink to="/settings/campus" className="navlink">
                The Campus
              </NavLink>
              {/* Under Settings rather than inside Aquatics or Shooting,
                  which is the whole point of 0221: a cart waiver and a
                  fitness-room waiver belong to no amenity, and two
                  modules each owning their own is what produced two
                  implementations. */}
              <NavLink to="/settings/waivers" className="navlink">
                Waivers
              </NavLink>
              <NavLink to="/settings/rules" className="navlink">
                The Club's Rules
              </NavLink>
              <NavLink to="/settings/rules-shown" className="navlink">
                Where Rules Appear
              </NavLink>
              <NavLink to="/settings/sample-data" className="navlink">
                Sample Data
              </NavLink>
              <NavLink to="/settings/export" className="navlink">
                Export Data
              </NavLink>
              <NavLink to="/settings/import" className="navlink">
                Import Data
              </NavLink>
              {atLeast('betterclub_support') && (
                <NavLink to="/settings/demo-clubs" className="navlink">
                  Demo Clubs
                </NavLink>
              )}

              <div className="sidebar__group">Accounting System</div>
              <NavLink to="/ledger/sync" className="navlink">
                Sync Status
              </NavLink>
              <NavLink to="/ledger/mapping" className="navlink">
                Account Mapping
              </NavLink>
              {atLeast('controller') && (
                <NavLink to="/ledger/settings" className="navlink">
                  Ledger Settings
                </NavLink>
              )}
            </>
          )}
        </div>

        <div className="sidebar__foot">
          <div>{me?.principal.role.replace('_', ' ')}</div>
          <button
            className="secondary small"
            style={{ marginTop: 8, width: '100%' }}
            onClick={signOut}
          >
            Sign out
          </button>
          {/* The tagline is BetterClub's, not the club's, so it sits with the
              vendor mark rather than under the club's name. */}
          <div className="sidebar__vendor">
            <BetterClubLockup height={24} />
            <span>YOUR CLUB, BETTER CONNECTED.</span>
          </div>
        </div>
      </nav>

      <div className="main">
        <ActingBanner />

        <header className="topbar">
          <button
            className="menu-button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-label={menuOpen ? 'Close the menu' : 'Open the menu'}
            aria-expanded={menuOpen}
            aria-controls="main-nav"
          >
            <span aria-hidden="true" />
            <span aria-hidden="true" />
            <span aria-hidden="true" />
          </button>
          {/*
            On a phone the club's own mark is inside a drawer nobody has
            open, so the top bar carries it instead of the name in type.
            The same rule as the drawer applies: the drawn wordmark only
            fits the club it was drawn for, and any other tenant gets its
            name set in type rather than somebody else's logo.
          */}
          {club?.brand_name === 'Club 1899' && (
            <Club1899Wordmark height={17} className="topbar__mark" />
          )}
          <strong className="topbar__name">{club?.legal_name}</strong>
          <span className="muted small">
            {club?.country_code} · {club?.base_currency}
            {club?.prices_include_tax ? ' · prices include tax' : ''}
          </span>
          <div className="topbar__spacer" />
        </header>

        <main className="content">
          <Routes>
            {/* A member signing in landed on the club dashboard and was
                told "this action requires the manager role or higher" —
                the first sentence the software said to them. Sending them
                somewhere they may actually be is a stopgap and marked as
                one: the real answer is a member home page, which belongs
                to core rather than to golf. Remove this line when it
                exists. */}
            <Route path="/" element={
              /* A member's front door is their own week, not a golf
                 page. Staff keep the operational dashboard — the two
                 answer completely different questions. */
              atLeast('staff') ? <DashboardPage /> : <HomePage />
            } />
            {/* The same page at an address that does not depend on who
                is asking, so the nav can link to it honestly. */}
            <Route path="/my-week" element={<HomePage />} />
            <Route path="/members" element={<MembersPage />} />
            <Route path="/members/new" element={<NewMemberPage />} />
            <Route path="/members/import" element={<MemberImportPage />} />
            {/* Before the :id route. Router v6 ranks a static segment
                above a dynamic one whatever the order, but a reader
                should not have to know that to be sure /members/birthdays
                is not an account whose id is the word "birthdays". */}
            <Route path="/members/birthdays" element={<BirthdaysPage />} />
            <Route path="/members/applications" element={<ApplicationsPage />} />
            <Route path="/members/:id" element={<MemberDetailPage />} />
            <Route path="/structure" element={<StructurePage />} />
            <Route path="/staff" element={<StaffPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/schedule" element={<SchedulePage />} />
            <Route path="/my-time" element={<MyTimePage />} />
            <Route path="/my-leave" element={<MyLeavePage />} />
            <Route path="/leave" element={<LeavePage />} />
            <Route path="/timesheets" element={<TimesheetsPage />} />
            <Route path="/labor-cost" element={<LaborCostPage />} />
            <Route path="/leave-liability" element={<LeaveLiabilityPage />} />
            <Route path="/weather-staffing" element={<WeatherStaffingPage />} />
            <Route path="/logins" element={<LoginsPage />} />
            <Route path="/golf/my" element={<MyGolfPage />} />
            <Route path="/golf/tee-sheet" element={<TeeSheetPage />} />
            <Route path="/golf/scorecard" element={<ScorecardPage />} />
            <Route path="/golf/card" element={<PlayingCardPage />} />
            <Route path="/golf/admin" element={<GolfAdminPage />} />
            <Route path="/shop" element={<ProShopPage />} />
            <Route path="/tennis/court-sheet" element={<CourtSheetPage />} />
            <Route path="/tennis/my-courts" element={<MyCourtsPage />} />
            <Route path="/tennis/admin" element={<TennisAdminPage />} />
            <Route path="/pool/deck" element={<DeckSheetPage />} />
            <Route path="/pool/admin" element={<PoolAdminPage />} />
            <Route path="/pool/my" element={<MyPoolPage />} />
            <Route path="/grounds" element={<AgronomyPage />} />
            {/* A member's own account. The id comes from the token, not
                from this path — see MyAccount.tsx. */}
            <Route path="/my-account" element={<MyAccountPage />} />
            <Route path="/fnb/terminal" element={<FnbTerminalPage />} />
            <Route path="/fnb/service" element={<FnbServicePage />} />
            <Route path="/fnb/menu" element={<FnbMenuPage />} />
            <Route path="/fnb/kitchen" element={<KitchenPage />} />
            <Route path="/fnb/recipes" element={<RecipesPage />} />
            <Route path="/boating/my" element={<MyBoatingPage />} />
            <Route path="/boating/dock-sheet" element={<DockSheetPage />} />
            <Route path="/boating/transient-log" element={<TransientLogPage />} />
            <Route path="/boating/admin" element={<MarinaAdminPage />} />
            <Route path="/shooting/my" element={<MyRangePage />} />
            <Route path="/shooting/range-sheet" element={<RangeSheetPage />} />
            <Route path="/shooting/admin" element={<ShootingAdminPage />} />
            <Route path="/finance/dues-schedule" element={<DuesSchedulePage />} />
            <Route path="/finance/assessments" element={<AssessmentsPage />} />
            <Route path="/finance/assessments/:id" element={<AssessmentDetailPage />} />
            <Route path="/charges/new" element={<PostChargePage />} />
            <Route path="/payments/new" element={<TakePaymentPage />} />
            <Route path="/finance/dues" element={<BillingRunsPage />} />
            <Route path="/finance/collections" element={<CollectionsPage />} />
            <Route path="/finance/trial-balance" element={<TrialBalancePage />} />
            <Route path="/finance/departmental-pl" element={<DepartmentalPLPage />} />
            <Route path="/finance/statements" element={<StatementsPage />} />
            <Route path="/finance/periods" element={<PeriodsPage />} />
            <Route path="/banking/accounts" element={<BankAccountsPage />} />
            <Route path="/banking/accounts/:id" element={<BankAccountDetailPage />} />
            <Route path="/banking/reconciliations/new" element={<NewReconciliationPage />} />
            <Route path="/banking/reconciliations/:id" element={<ReconciliationPage />} />
            <Route path="/purchasing" element={<PurchasingPage />} />
            <Route path="/ap/vendors" element={<VendorsPage />} />
            <Route path="/ap/vendors/:id" element={<VendorDetailPage />} />
            <Route path="/ap/invoices" element={<InvoicesPage />} />
            <Route path="/ap/invoices/new" element={<NewInvoicePage />} />
            <Route path="/ap/invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="/ap/runs" element={<PaymentRunsPage />} />
            <Route path="/ap/runs/:id" element={<PaymentRunDetailPage />} />
            <Route path="/ap/approval-rules" element={<ApprovalRulesPage />} />
            <Route path="/ap/reports" element={<ApReportsPage />} />
            <Route path="/governance/bylaws" element={<BylawsPage />} />
            <Route path="/governance/board" element={<BoardPage />} />
            <Route path="/governance/meetings" element={<MeetingsPage />} />
            <Route path="/settings/campus" element={<CampusPage />} />
            <Route path="/settings/waivers" element={<WaiversPage />} />
            <Route path="/settings/rules" element={<ClubRulesPage />} />
            <Route path="/settings/rules-shown" element={<BylawNoticesPage />} />
            <Route path="/settings/sample-data" element={<SampleDataPage />} />
            <Route path="/settings/export" element={<ExportDataPage />} />
            <Route path="/settings/import" element={<ImportDataPage />} />
            <Route path="/settings/demo-clubs" element={<DemoClubsPage />} />
            <Route path="/coa" element={<ChartOfAccountsPage />} />
            <Route path="/coa/import" element={<CoaImportPage />} />
            <Route path="/ledger/sync" element={<LedgerSyncPage />} />
            <Route path="/ledger/mapping" element={<LedgerMappingPage />} />
            <Route path="/ledger/settings" element={<LedgerSettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>

        {/*
          On a phone the sidebar — and with it the vendor mark that sits
          in its foot — is behind a drawer nobody has open. This puts the
          mark back where it can be seen, pinned to the bottom of the
          screen. Above the home indicator, not under it.
        */}
        <footer className="mobile-foot">
          <BetterClubLockup height={20} />
          <span>YOUR CLUB, BETTER CONNECTED.</span>
        </footer>
      </div>
    </div>
  );
}

/**
 * You are inside somebody else's club.
 *
 * The riskiest thing an operator does is forget which club they are
 * looking at, because every screen below this line looks exactly as it
 * does to the club's own staff — which is the point of being able to
 * open one, and exactly why it needs saying continuously rather than
 * once at the door.
 */
function ActingBanner() {
  const acting = getActingAs();
  const { signOut } = useSession();
  if (!acting) return null;

  return (
    <div className="acting">
      <strong>You are inside {acting.club_name}</strong>
      <span>
        as support, signed in by {acting.operator_email}. Everything you do here is this
        club's record.
      </span>
      <div className="spacer" />
      <button
        className="secondary small"
        onClick={() => {
          clearActingAs();
          signOut();
          window.location.assign('/operator');
        }}
      >
        Leave
      </button>
    </div>
  );
}

function Gate() {
  const { loading, me } = useSession();
  if (loading) {
    return (
      <div style={{ padding: 40, maxWidth: 480 }}>
        <Loading rows={4} />
      </div>
    );
  }
  return me ? <Shell /> : <Login />;
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/*
          Outside the club session entirely, and outside its provider. An
          operator has no club, so a tree that begins by asking "which club
          are you signed into" cannot contain this one — it would spend
          every render deciding it had been signed out.
        */}
        <Route path="/operator/*" element={<OperatorApp />} />
        <Route
          path="/s/:code"
          element={
            <SessionProvider>
              <ScanLandingPage />
            </SessionProvider>
          }
        />
        <Route
          path="*"
          element={
            <SessionProvider>
              <Gate />
            </SessionProvider>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
